import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Heart, MapPin, Mail, Star } from "lucide-react";
import { BRAND, ASSETS } from "../lib/brand";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/programs", label: "Programs" },
  { to: "/events", label: "Events" },
  { to: "/resources", label: "Resources" },
  { to: "/community", label: "Community" },
  { to: "/donate", label: "Donate" },
  { to: "/contact", label: "Contact" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => { setOpen(false); }, [location.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`sticky top-0 z-40 transition-all ${scrolled ? "bg-[#FDFBF7]/90 backdrop-blur-xl border-b border-black/5" : "bg-transparent"}`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="flex items-center justify-between h-20">
          <Link to="/" data-testid="brand-logo-link" className="flex items-center gap-3 group">
            <img src={ASSETS.logo} alt="Stars of Autism logo"
              className="h-14 w-14 object-contain group-hover:rotate-6 transition-transform duration-500" />
            <div className="leading-tight">
              <div className="font-heading text-[1.05rem] font-bold tracking-tight text-[#1F2937]">Stars of Autism</div>
              <div className="text-[0.72rem] text-[#6B7280] tracking-wide uppercase">Marion, AR</div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-1" aria-label="Primary">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `px-4 py-2 rounded-full text-sm font-medium transition ${
                    isActive ? "bg-[#1F2937] text-white" : "text-[#1F2937] hover:bg-black/5"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <Link
              to="/donate"
              data-testid="nav-donate-cta"
              className="inline-flex items-center gap-2 rounded-full bg-[#2196F3] hover:bg-[#1e88e5] text-white px-5 py-2.5 text-sm font-semibold shadow-sm hover:shadow-md hover:-translate-y-0.5"
            >
              <Heart className="w-4 h-4" strokeWidth={2} /> Give
            </Link>
          </div>

          <button
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
            className="lg:hidden p-2 rounded-full hover:bg-black/5"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-[#FDFBF7] border-t border-black/5">
          <div className="px-6 py-4 flex flex-col gap-1">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `px-4 py-3 rounded-2xl text-base font-medium ${
                    isActive ? "bg-[#1F2937] text-white" : "text-[#1F2937] hover:bg-black/5"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <Link
              to="/donate"
              data-testid="mobile-nav-donate-cta"
              className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-[#2196F3] text-white px-5 py-3 text-base font-semibold"
            >
              <Heart className="w-4 h-4" /> Give a Puzzle Piece
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export const Footer = () => {
  return (
    <footer data-testid="site-footer" className="mt-24 bg-[#1F2937] text-[#F4F1EA]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-16 grid md:grid-cols-4 gap-10">
        <div className="md:col-span-2">
          <div className="flex items-center gap-3">
            <img src={ASSETS.logo} alt="logo" className="h-14 w-14 object-contain" />
            <div>
              <div className="font-heading text-xl font-bold tracking-tight">Stars of Autism</div>
              <div className="text-sm text-white/60">Different, not less.</div>
            </div>
          </div>
          <p className="mt-5 text-sm text-white/70 max-w-md leading-relaxed">
            A community-led non-profit building a permanent, sensory-friendly home for Autism families in Marion, Arkansas.
          </p>
          <div className="mt-5 flex items-center gap-1 text-amber-300">
            <Star className="w-4 h-4 fill-current" strokeWidth={0} />
            <Star className="w-4 h-4 fill-current" strokeWidth={0} />
            <Star className="w-4 h-4 fill-current" strokeWidth={0} />
            <Star className="w-4 h-4 fill-current" strokeWidth={0} />
            <Star className="w-4 h-4 fill-current" strokeWidth={0} />
          </div>
        </div>

        <div>
          <h4 className="font-heading font-semibold text-base mb-4">Explore</h4>
          <ul className="space-y-2 text-sm text-white/75">
            {NAV.map((n) => (
              <li key={n.to}>
                <Link to={n.to} className="hover:text-white" data-testid={`footer-link-${n.label.toLowerCase()}`}>{n.label}</Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="font-heading font-semibold text-base mb-4">Reach Us</h4>
          <ul className="space-y-3 text-sm text-white/75">
            <li className="flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5 text-[#FFB300]" strokeWidth={1.6} /><span>Marion, Arkansas</span></li>
            <li className="flex items-start gap-2"><Mail className="w-4 h-4 mt-0.5 text-[#2196F3]" strokeWidth={1.6} /><span>via Contact form</span></li>
            <li className="flex items-start gap-2"><Heart className="w-4 h-4 mt-0.5 text-[#E53935]" strokeWidth={1.6} /><Link to="/donate" className="hover:text-white">Donate via Cash App</Link></li>
          </ul>
          <a href="https://www.facebook.com/share/1DGrahrNKh/" target="_blank" rel="noreferrer"
             data-testid="footer-facebook-link"
             className="mt-5 inline-block text-xs text-white/60 hover:text-white underline-offset-4 hover:underline">
            Follow our story on Facebook →
          </a>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-6 flex flex-col md:flex-row md:items-center md:justify-between gap-2 text-xs text-white/50">
          <div>© {new Date().getFullYear()} {BRAND.name}. Built with care for our community.</div>
          <div className="flex items-center gap-4">
            <Link to="/admin" data-testid="footer-admin-link" className="hover:text-white/80">Admin</Link>
            <span>Marion, AR</span>
          </div>
        </div>
      </div>
    </footer>
  );
};
